import { useStoreItemApi } from '../../hooks/useStoreItemApi';
import { AddOrEditItemRequestSchema, BulkUploadItemAddedByEnum } from '../../client-sdk'; // Adjust path as needed
import { useSnackbar } from '../Stateless/SnackBarProvider';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const useAddStoreItem = () => {
  const queryClient = useQueryClient();
  const { addStoreItem } = useStoreItemApi();
  const { showMessage } = useSnackbar();
  return useMutation({
    mutationKey: ['CreateStoreItem'],
    mutationFn: (data: AddOrEditItemRequestSchema) => addStoreItem(data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Store item added successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to add Store item',
        'error'
      );
    },
  });
};
type Params = {
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  page: number;
  limit: number;
  search?: string;
  stockAvailability?: string;
  addedBy?: string;
  storeId?: string;
  storeType?: string;
};

export const useGetAllStoreItem = (queryParams: Params) => {
  const { getAllStoreItem } = useStoreItemApi();

  const {
    data,
    isLoading: isFetching,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: [
      'getStoreItem',
      queryParams
    ],
    queryFn: () => {
      return getAllStoreItem(queryParams);
    },
    enabled: Boolean(
      queryParams &&
      queryParams.page !== undefined &&
      queryParams.limit !== undefined &&
      queryParams.sortBy !== undefined &&
      queryParams.sortOrder !== undefined
    ),
  });


  const isLoading = isFetching || isRefetching;

  return {
    data,
    isLoading,
    refetch
  };
};

export const useBlockUnblockStoreItemById = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { blockOrUnblockStoreItemById } = useStoreItemApi();

  return useMutation({
    mutationKey: ['blockUnblockStoreItem'],
    mutationFn: ({ id, data }: { id: string[]; data: any }) =>
      blockOrUnblockStoreItemById({ id, status: data.status }),

    onSuccess: (_response, _variables) => {
      const status = _variables.data.status;
      const action = status === 'blocked' ? 'blocked' : 'unblocked';
      showMessage(`Store Item ${action} successfully`, 'success');

      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
    },

    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update Store item status',
        'error'
      );
    },
  });
};

export const useDeleteStoreItemById = () => {
  const queryClient = useQueryClient();
  const { deleteStoreItemById } = useStoreItemApi();
  const { showMessage } = useSnackbar();

  return useMutation({
    mutationKey: ['DeleteStoreItem'],

    mutationFn: (ids: string[]) => {
      return deleteStoreItemById(ids);
    },
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Store item deleted successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to delete Store item',
        'error'
      );
    },
  });
};

export const useEditStoreItem = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { editStoreItem } = useStoreItemApi();

  return useMutation({
    mutationKey: ['editstoreitem'],
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      editStoreItem(id, data),
    onSuccess: (response: any) => {
      showMessage(
        response?.message || 'Store Item Updated successfully',
        'success'
      );
      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.detail?.[0]?.type ||
          'Failed to update the SubAdmin',
        'error'
      );
    },
  });
};

export const useBulkUploadItem = () => {
  const queryClient = useQueryClient();
  const { showMessage } = useSnackbar();
  const { bulkUploadItem } = useStoreItemApi();

  return useMutation({
    mutationKey: ['bulkUploadItem'],
    mutationFn: ({
      storeId,
      addedBy,
      file,
      merchantId,
    }: {
      storeId: string;
      addedBy: BulkUploadItemAddedByEnum;
      file: File;
      merchantId?: string;
    }) => bulkUploadItem(storeId, addedBy, file, merchantId),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({
        queryKey: ['getStoreItem'],
        exact: false,
      });
      return response;
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to upload items',
        'error'
      );
      throw error;
    },
  });
};

export const useGetBulkUploadStatus = (jobId: string | null) => {
  const { getBulkUploadStatus } = useStoreItemApi();

  return useQuery({
    queryKey: ['getBulkUploadStatus', jobId],
    queryFn: () => getBulkUploadStatus(jobId!),
    enabled: Boolean(jobId),
    refetchInterval: (query) => {
      const data = query.state.data as any;
      // Poll every 2 seconds if job is still processing
      if (data?.status === 'queued' || data?.status === 'processing') {
        return 2000;
      }
      // Stop polling if completed or failed
      return false;
    },
  });
};

export const useDownloadBulkUploadTemplate = () => {
  const { showMessage } = useSnackbar();
  const { downloadBulkUploadTemplate } = useStoreItemApi();

  return useMutation({
    mutationKey: ['downloadBulkUploadTemplate'],
    mutationFn: (storeId: string) =>
      downloadBulkUploadTemplate(storeId),
    onSuccess: () => {
      showMessage('Template downloaded successfully', 'success');
    },
    onError: (error: any) => {
      showMessage(
        error.response?.data?.message || 'Failed to download template',
        'error'
      );
    },
  });
};
